import { useLayoutEffect, useState } from 'react';
import type { GanttQueue, QueueTaskNode } from '../../types';
import { Polyline } from './Polyline';
import { getGanttDepsRelationPoints, getGraphElementById } from './utils';
import { useIsOpenTaskDetailsDrawer } from '../../../../../../pages/generic/Products/components/TaskDetailDrawer/TaskDetailsDrawer';
import { useProductionPlanQueryParams } from '../../../hooks';
import { useProductionPlanSettings } from '../../../components/SettingsDrawer';

type StyledSVGProps = {
  ganttQueues: GanttQueue[];
};

/**
 * Return array of queues id's which has parents/children of selected task
 */
export const useSelectedTaskDependencyQueuesIds = (ganttQueues: GanttQueue[]) => {
  const showDependencies = useProductionPlanSettings((state) => state.gantt.showDependencies);
  const { queryParams } = useProductionPlanQueryParams();

  const queueId = queryParams.queueId;
  const taskId = queryParams.taskId;

  if (!showDependencies || !queueId || !taskId) {
    return [];
  }

  const queue = ganttQueues.find((_) => _.id === queueId);
  const sourceTask = queue?.queueNodes.filter((_) => _.id).find((_) => _.id === taskId) as QueueTaskNode | undefined;

  const childrenQueues = (sourceTask?.dependenciesDetails?.children || []).map((_) => _.queueId);
  const parentQueues = (sourceTask?.dependenciesDetails?.parents || []).map((_) => _.queueId);

  return [queueId, ...childrenQueues, ...parentQueues].filter(Boolean);
};

export const GanttDependenciesGraph = ({ ganttQueues }: StyledSVGProps) => {
  const showDependencies = useProductionPlanSettings((state) => state.gantt.showDependencies);
  const isOpen = useIsOpenTaskDetailsDrawer();
  const { queryParams } = useProductionPlanQueryParams();

  const queueId = queryParams.queueId;
  const taskId = queryParams.taskId;
  const shouldShow = Boolean(showDependencies && isOpen && queueId && taskId);

  // Remasure after force-mounted dependency nodes are in the DOM (same update as this graph).
  const [layoutTick, setLayoutTick] = useState(0);
  const [wasShowing, setWasShowing] = useState(false);

  if (shouldShow !== wasShowing) {
    setWasShowing(shouldShow);
    if (!shouldShow) {
      setLayoutTick(0);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: remasure when selection/queues change
  useLayoutEffect(() => {
    if (!shouldShow) {
      return;
    }
    const frameId = requestAnimationFrame(() => {
      setLayoutTick((tick) => tick + 1);
    });
    return () => cancelAnimationFrame(frameId);
  }, [shouldShow, queueId, taskId, ganttQueues]);

  if (!shouldShow || layoutTick === 0) {
    return null;
  }

  const queue = ganttQueues.find((_) => _.id === queueId);
  const queueIndex = ganttQueues.findIndex((_) => _.id === queueId);
  const sourceTask = queue?.queueNodes
    .filter((_) => _.id) // we need to remove empty nodes
    // disable === because we need to compare undefined with null
    // eslint-disable-next-line eqeqeq
    .find((_) => _.id === taskId) as QueueTaskNode | undefined;

  const hasDependencies =
    sourceTask?.dependenciesDetails?.children?.some((_) => _.queueId) ||
    sourceTask?.dependenciesDetails?.parents?.some((_) => _.queueId);

  const root = getGraphElementById(taskId);

  if (hasDependencies && root && sourceTask) {
    const startPoint = getGanttDepsRelationPoints(root, queueIndex, sourceTask.nodeWidth);

    if (startPoint) {
      return (
        <svg
          className="pointer-events-none absolute top-0 right-0 bottom-0 left-0 h-full w-full"
          fill="none"
          role="presentation"
          xmlns="http://www.w3.org/2000/svg"
        >
          {sourceTask.dependenciesDetails?.parents
            ?.filter((_) => _.queueId)
            .map((target) => (
              <Polyline
                ganttQueues={ganttQueues}
                key={target.taskId}
                sourceQueueIndex={queueIndex}
                sourceTask={sourceTask}
                startPoint={startPoint}
                targetTask={target}
              />
            ))}
          {sourceTask.dependenciesDetails?.children
            ?.filter((_) => _.queueId)
            .map((target) => (
              <Polyline
                ganttQueues={ganttQueues}
                key={target.taskId}
                sourceQueueIndex={queueIndex}
                sourceTask={sourceTask}
                startPoint={startPoint}
                targetTask={target}
              />
            ))}
        </svg>
      );
    }
  }
  return null;
};
